import React, { useState, useMemo } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { emailCampaignEntity } from '../../config/entities/emailCampaigns';
import { crmService } from '../../services/crmCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
import { ViewModal } from '../../components/common/ViewModal';
import { CreateCampaignModal } from '../../components/crm/CreateCampaignModal';
import { usePermissions } from '../../components/navigation/AccessControl';
import api from '../../services/api';

export const EmailCampaignsPage: React.FC = () => {
  const { t } = useLanguage();
  const { isPlatformAdmin, user } = usePermissions();
  
  // Modal state management
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Key to force table refresh

  // Use Dedicated CRM Service (same as dashboard) with wrapper for FlexibleCRUDTable compatibility
  const campaignService = {
    getAll: (params: any) => crmService.getEmailCampaigns(params),
    get: (id: string) => crmService.getEmailCampaignById(id),
    create: (data: any) => crmService.createEmailCampaign(data),
    update: (id: string, data: any) => crmService.updateEmailCampaign(id, data),
    delete: (id: string) => crmService.deleteEmailCampaign(id)
  };

  // CRUD handlers
  const handleViewCampaign = (campaign: any) => {
    setSelectedCampaign(campaign);
    setIsViewModalOpen(true);
  };

  const handleEditCampaign = (campaign: any) => {
    // Check if campaign can be edited
    if (!canEditCampaign(campaign)) {
      if (campaign.sent_date || campaign.status === 'sent' || campaign.status === 'sending') {
        alert(t('crm.campaigns.cannotEditSentCampaign') || 'Cannot edit a campaign whose emails have been sent.');
      } else if (campaign.scheduled_date) {
        const scheduledDate = new Date(campaign.scheduled_date);
        const now = new Date();
        if (scheduledDate < now) {
          alert(t('crm.campaigns.cannotEditPastCampaign') || 'Cannot edit a campaign whose scheduled time has passed.');
        }
      }
      return;
    }
    
    setSelectedCampaign(campaign);
    setIsEditModalOpen(true);
  };
  
  // Check if a campaign can be edited
  // Campaigns cannot be edited if:
  // 1. Scheduled time has passed, OR
  // 2. Emails have been sent (sent_date exists or status is 'sent'/'sending')
  const canEditCampaign = (campaign: any): boolean => {
    // If emails have been sent, cannot edit
    if (campaign.sent_date) {
      return false;
    }
    
    // If status indicates emails have been sent or are being sent, cannot edit
    if (campaign.status === 'sent' || campaign.status === 'sending') {
      return false;
    }
    
    // If scheduled and scheduled time has passed, cannot edit
    if (campaign.scheduled_date) {
      const scheduledDate = new Date(campaign.scheduled_date);
      const now = new Date();
      if (scheduledDate < now) {
        return false;
      }
    }
    
    return true; // Can edit otherwise
  };

  const handleDeleteCampaign = async (campaign: any) => {
    // Confirmation is handled by FlexibleCRUDTable component
    try {
      await campaignService.delete(campaign.id);
      // The table will automatically refresh
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert(t('crm.campaigns.deleteError'));
    }
  };

  const handleCreateCampaign = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateNewCampaign = async () => {
    // This is now handled by CreateCampaignModal
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedCampaign(null);
    // Force table refresh by updating the key
    setRefreshKey(prev => prev + 1);
  };

  const handleCopyCampaign = async (campaign: any) => {
    try {
      // Get user's timezone from profile
      let userTimezone = 'UTC';
      try {
        const response = await api.get('/users/profile');
        const userProfile = response.data.data;
        userTimezone = userProfile?.preferences?.timezone || 'UTC';
      } catch (err) {
        console.error('Error loading user timezone:', err);
        // Default to UTC if profile load fails
      }

      // Create copy data
      const copyData: any = { ...campaign };

      // Remove system fields that shouldn't be copied
      delete copyData.id;
      delete copyData.created_at;
      delete copyData.updated_at;
      delete copyData.tenant_id;
      delete copyData.sent_date;
      
      // Remove computed/display fields
      delete copyData.created_by_name;
      delete copyData.template_name;
      delete copyData.open_rate;
      delete copyData.click_rate;
      delete copyData.bounce_rate;
      delete copyData.opened_count;
      delete copyData.clicked_count;
      delete copyData.replied_count;
      delete copyData.sent_count;
      delete copyData.delivered_count;
      delete copyData.bounced_count;
      delete copyData.unsubscribed_count;

      // Set status to 'scheduled'
      copyData.status = 'scheduled';

      // Set scheduled_date to 15 minutes from now
      const now = new Date();
      const scheduledDate = new Date(now.getTime() + 15 * 60 * 1000); // 15 minutes from now
      
      // Format as YYYY-MM-DDTHH:mm:ss (naive timestamp string, matching CreateCampaignModal format)
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');
      const hours = String(scheduledDate.getHours()).padStart(2, '0');
      const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
      const seconds = String(scheduledDate.getSeconds()).padStart(2, '0');
      copyData.scheduled_date = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      
      // Set scheduled_timezone to user's timezone
      copyData.scheduled_timezone = userTimezone;

      // Generate a unique name for the copy
      const nameField = emailCampaignEntity.nameField || 'name';
      if (copyData[nameField]) {
        // Extract base name (remove any existing "Copy" suffix)
        const baseName = copyData[nameField].replace(/\s+\(Copy.*?\)$/, '').replace(/\s+Copy$/, '');
        copyData[nameField] = `${baseName} Copy`;
      }

      // Create the copied campaign
      await campaignService.create(copyData);
      
      // Force table refresh
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error copying campaign:', error);
      alert(t('crm.campaigns.copyError') || 'Failed to copy campaign');
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCampaign(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  // Create filtered entity based on user role
  // Hide open_rate and click_rate for non-platform-admin users
  const filteredEntity = useMemo(() => {
    if (isPlatformAdmin()) {
      // Platform admins see everything
      return emailCampaignEntity;
    }
    
    // Non-platform-admin users: hide open_rate and click_rate
    return {
      ...emailCampaignEntity,
      tableColumns: emailCampaignEntity.tableColumns?.filter(
        (col: any) => col.key !== 'open_rate' && col.key !== 'click_rate'
      ) || [],
      fields: emailCampaignEntity.fields.map((field: any) => {
        // Mark open_rate and click_rate as hidden for non-platform-admin users
        if (field.key === 'open_rate' || field.key === 'click_rate') {
          return { ...field, hidden: true };
        }
        return field;
      })
    };
  }, [user?.role]); // Depend on user role instead of function

  // Filter fields for ViewModal based on user role and reorder them
  // Description fields first, then basic statistics (total_recipients, sent_count, delivered_count),
  // then advanced statistics (only for platform admins)
  const getViewModalFields = () => {
    const baseFields = filteredEntity.fields.filter(f => !f.hidden).map(f => ({
      key: f.key || f.name || '',
      label: f.label,
      type: f.type
    }));
    
    // Separate fields into description fields, basic statistics, and advanced statistics
    const descriptionFields = [
      'name',
      'subject',
      'email_content',
      'template_id',
      'status',
      'scheduled_date',
      'sent_date'
    ];
    
    // Basic statistics that all users can see
    const basicStatisticsFields = [
      'total_recipients',
      'sent_count',
      'delivered_count'
    ];
    
    // Advanced statistics that only platform admins can see
    const advancedStatisticsFields = [
      'opened_count',
      'clicked_count',
      'replied_count',
      'bounced_count',
      'unsubscribed_count',
      'open_rate',
      'click_rate',
      'bounce_rate'
    ];
    
    // Create ordered array: description fields first, then basic statistics, then advanced statistics
    const orderedFields: Array<{ key: string; label: string; type: string }> = [];
    
    // Add description fields in order
    descriptionFields.forEach(key => {
      const field = baseFields.find(f => f.key === key);
      if (field) {
        orderedFields.push(field);
      }
    });
    
    // Add basic statistics fields in order (all users see these)
    basicStatisticsFields.forEach(key => {
      const field = baseFields.find(f => f.key === key);
      if (field) {
        orderedFields.push(field);
      }
    });
    
    // Add advanced statistics fields in order (only platform admins see these)
    if (isPlatformAdmin()) {
      advancedStatisticsFields.forEach(key => {
        const field = baseFields.find(f => f.key === key);
        if (field) {
          orderedFields.push(field);
        }
      });
    }
    
    // Add any remaining fields that weren't in any category
    baseFields.forEach(field => {
      if (!descriptionFields.includes(field.key) && 
          !basicStatisticsFields.includes(field.key) && 
          !advancedStatisticsFields.includes(field.key)) {
        orderedFields.push(field);
      }
    });
    
    return orderedFields;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full py-6">
        {/* Main Content */}
        <div className="px-4 py-6">
          {/* Information Notice about Campaign Editing */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              {t('crm.campaigns.editRulesTitle') || 'Campaign Editing Rules'}
            </h3>
            <div className="text-sm text-blue-800 space-y-1">
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>{t('crm.campaigns.defaultScheduled') || 'New or copied campaigns are by default scheduled 15 minutes from now.'}</li>
                <li>{t('crm.campaigns.cannotEditSent') || 'Campaigns once sent can\'t be edited.'}</li>
              </ul>
            </div>
          </div>

          <FlexibleCRUDTable
            key={refreshKey} // Force refresh when key changes
            entity={filteredEntity}
            service={campaignService}
            onView={handleViewCampaign}
            onEdit={handleEditCampaign}
            canEdit={canEditCampaign}
            onDelete={handleDeleteCampaign}
            onCopy={handleCopyCampaign}
            onCreate={handleCreateCampaign}
            onOperationComplete={() => {
              // Force refresh after any operation (delete, etc.)
              setRefreshKey(prev => prev + 1);
            }}
            searchPlaceholder={t('crm.campaigns.searchPlaceholder')}
            showPagination={true}
            showSearch={true}
          />
        </div>
      </div>

      {/* View Campaign Modal */}
      {isViewModalOpen && selectedCampaign && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseViewModal}
          title={`View ${emailCampaignEntity.displayNameSingular}`}
          data={selectedCampaign}
          fields={getViewModalFields()}
        />
      )}

      {/* Edit Campaign Modal - Now using CreateCampaignModal */}
      {isEditModalOpen && selectedCampaign && (
        <CreateCampaignModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleCreateNewCampaign}
          campaignId={selectedCampaign.id}
        />
      )}

      {/* Create Campaign Modal */}
      {isCreateModalOpen && (
        <CreateCampaignModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          onSuccess={handleCreateNewCampaign}
        />
      )}
    </div>
  );
};